import type { PublicId } from 'flow/store/engine/types';
import { generateOperations } from '../../changelog';
import { executePredicatedProcessors } from '../../lib/predicatedProcessor';
import createOperationProcessors, { createChangesByOperationBuffer } from '../../operationProcessors';
import { Operation } from '../../types';
import type {
  ChangesByOperation,
  RFC6902Operation,
  ChangelogInputBundle,
} from '../../types';
import mergeDiffFormatter from './mergeDiffFormatter';
import type { MergeDiffResult } from './types';
import { convertOpToActionModifiedOp, isActionRuleGroupingModifiedChange } from '../../guards';

/**
 * Processes a list of operations and creates Changes representing them.
 * @param operations operations that happened from versionA to versionB
 * @param versions a bundle of versions to compare
 * @returns a record of changes keyed by operation type.
 */
function computeChangesPerOperation(args: {
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
  operations: RFC6902Operation[];
}): ChangesByOperation {
  const { operations, A, B } = args;
  const changesBuffer = createChangesByOperationBuffer();
  const operationProcessors = createOperationProcessors({ changesBuffer, A, B });
  executePredicatedProcessors({ items: operations, processorRegistry: operationProcessors });
  return changesBuffer.package();
}

export default function generateMergeDiff(args: {
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
  isDiffOrLinkedSnippetUpgrade?: boolean;
}): MergeDiffResult {
  const { A, B, isDiffOrLinkedSnippetUpgrade = false } = args;
  const operations = generateOperations({ A, B });

  const {
    add: addedChanges,
    modify: modifiedChanges,
    remove: removedChanges,
  } = computeChangesPerOperation({ operations, A, B });

  // Special Case:
  // we move the modified action rule to be handled by the added formatter
  // since we will only be adding rules at this time
  const ruleModifiedOps = modifiedChanges.filter(change => (
    isActionRuleGroupingModifiedChange(change)
  ));
  const adjustedAddedChanges = [...addedChanges, ...ruleModifiedOps];

  // Special case if upgrading a linked snippet or in diff-view:
  // Any non-action changes that do not correspond to an 'added' action are treated as modified
  // action changes, since they are modifying an action that exists in the previous snippet version
  const adjustedModifiedChanges = [...modifiedChanges];
  if (isDiffOrLinkedSnippetUpgrade) {
    const addedActionPublicIdSet = new Set<PublicId>();
    for (const change of addedChanges) {
      if (change.descriptor === 'action') {
        addedActionPublicIdSet.add(change.actionPublicId);
      }
    }
    for (const change of [...addedChanges, ...modifiedChanges, ...removedChanges]) {
      if (
        change.descriptor !== 'action'
        && !addedActionPublicIdSet.has(change.actionPublicId)
      ) {
        adjustedModifiedChanges.push(convertOpToActionModifiedOp(change));
      }
    }
  }

  return {
    added: new Set<PublicId>(
      mergeDiffFormatter({
        A,
        B,
        changes: adjustedAddedChanges,
        formattedOperation: Operation.Add,
      }),
    ),
    modified: new Set<PublicId>(
      mergeDiffFormatter({
        A,
        B,
        changes: adjustedModifiedChanges,
        formattedOperation: Operation.Modify,
      }),
    ),
    removed: new Set<PublicId>(
      mergeDiffFormatter({
        A,
        B,
        changes: removedChanges,
        formattedOperation: Operation.Remove,
      }),
    ),
  };
}
